import type { Dictionary, Translation } from '@/i18n/types';

const ro = {
  heading: 'Calendar de evenimente',
  seeOnMap: 'Vezi pe hartă',
  listAria: 'Lista evenimentelor',
  emptyState: 'Nu există înregistrări deocamdată. Reveniți mai târziu.',
  filteredEmptyState: 'Niciun eveniment nu corespunde filtrelor selectate.',
  periodEmptyState: 'Nu există evenimente în perioada aleasă.',
  loadFailed: 'Calendarul nu a putut fi încărcat. Încercați din nou.',
  paginationAria: 'Paginare',
  previous: 'Anterior',
  next: 'Următor',
  pageAria: 'Pagina {{page}}',
  currentPageAria: 'Pagina {{page}}, pagina curentă',
  pastBadge: 'Încheiat',
  cancelledBadge: 'Anulat',
  cancelledNotice: 'Evenimentul a fost anulat. Înscrierile sunt închise.',
  filterRegion: 'Raion',
  filterTheme: 'Tematică',
  filterKind: 'Tip activitate',
  filterOrganizer: 'Organizator',
  filterRegionAria: 'Filtrează după raion',
  filterThemeAria: 'Filtrează după tematică',
  filterKindAria: 'Filtrează după tip de activitate',
  filterOrganizerAria: 'Filtrează după organizator',
  filterAllRegions: 'Toate raioanele',
  filterAllThemes: 'Toate tematicile',
  filterAllKinds: 'Toate tipurile de activitate',
  filterAllOrganizers: 'Toți organizatorii',
  filtersAria: 'Filtre pentru calendarul evenimentelor',
  noEvents: 'Niciun eveniment',
  resultsCount: '{{count}} evenimente',
  resultsCount_one: '{{count}} eveniment',
  resultsCount_few: '{{count}} evenimente',
  resultsCount_other: '{{count}} de evenimente',
  mediaArchiveTag: 'Arhivă media',

  kindDEMONSTRATION: 'Demonstrație practică',
  kindVISIT: 'Vizită tematică',
  kindWORKSHOP: 'Atelier',
  kindCONFERENCE: 'Conferință',
  kindTRAINING_EVENT: 'Sesiune de instruire',
  kindSCIENTIFIC: 'Eveniment științific',

  backToCalendar: 'Înapoi la calendar',
  objectives: 'Obiective',
  agenda: 'Agenda',
  program: 'Program',
  speakers: 'Lectori și specialiști implicați',
  partners: 'Parteneri',
  acceptedPapers: 'Lucrări acceptate',
  documents: 'Documente',
  mediaArchive: 'Arhivă media',
  videoUnsupported: 'Browserul nu poate reda acest video. Îl puteți descărca de sub player.',
  downloadFile: 'Descarcă',
  downloadFileAria: 'Descarcă {{name}}',
  themes: 'Tematici',
  eligibility: 'Criterii de eligibilitate',
  participation: 'Participare',
  alreadyRegistered: 'Sunteți înscris la acest eveniment.',
  cancelRegistration: 'Retrage înscrierea',
  cancelRegistrationConfirm:
    'Retrageți înscrierea la acest eveniment? Vă puteți înscrie din nou până la începutul lui.',
  registrationCancelled: 'Înscrierea a fost retrasă.',
  registrationOpen: 'Înscrierea este deschisă pentru utilizatorii autentificați.',
  register: 'Înscrie-te la eveniment',
  registered: 'Înscrierea a fost înregistrată. Detaliile sosesc prin M-Notify.',
  registerSignIn: 'Autentificați-vă pentru înscriere:',
  registerSignInLink: 'Autentificare M-Pass',
  registerFailed: 'Înscrierea nu a putut fi înregistrată (posibil sunteți deja înscris).',
  concluded: 'Evenimentul s-a încheiat',
  concludedWithMedia: 'Evenimentul s-a încheiat — materialele sunt disponibile în arhiva media.',
  feedbackHeading: 'Evaluează evenimentul',
  feedbackRatingLabel: 'Notă (1-5)',
  feedbackCommentsLabel: 'Comentarii (opțional)',
  feedbackPresentationLabel: 'Calitatea prezentărilor (1-5)',
  feedbackNeedsLabel: 'De ce instruire aveți nevoie în continuare? (opțional)',
  feedbackSubmit: 'Trimite evaluarea',
  feedbackSubmitting: 'Se trimite...',
  feedbackAlready: 'Ați evaluat acest eveniment: {{rating}}/5.',
  feedbackError: 'Evaluarea nu a putut fi trimisă.',
  ratingsSuffix: 'evaluări ale participanților',
  presentationRatings: 'Calitatea prezentărilor',
  noRatings: 'Fără evaluări încă.',
  notFound: 'Evenimentul nu a fost găsit.',
  loading: 'Se încarcă…',
} as const;

const en = {
  heading: 'Events calendar',
  seeOnMap: 'See on the map',
  listAria: 'List of events',
  emptyState: 'No records yet. Please check back later.',
  filteredEmptyState: 'No events match the selected filters.',
  periodEmptyState: 'There are no events in the selected period.',
  loadFailed: 'The calendar could not be loaded. Please try again.',
  paginationAria: 'Pagination',
  previous: 'Previous',
  next: 'Next',
  pageAria: 'Page {{page}}',
  currentPageAria: 'Page {{page}}, current page',
  pastBadge: 'Concluded',
  cancelledBadge: 'Cancelled',
  cancelledNotice: 'This event has been cancelled. Registration is closed.',
  filterRegion: 'District',
  filterTheme: 'Theme',
  filterKind: 'Activity type',
  filterOrganizer: 'Organiser',
  filterRegionAria: 'Filter by district',
  filterThemeAria: 'Filter by theme',
  filterKindAria: 'Filter by activity type',
  filterOrganizerAria: 'Filter by organiser',
  filterAllRegions: 'All districts',
  filterAllThemes: 'All themes',
  filterAllKinds: 'All activity types',
  filterAllOrganizers: 'All organisers',
  filtersAria: 'Event calendar filters',
  noEvents: 'No events',
  resultsCount: '{{count}} events',
  resultsCount_one: '{{count}} event',
  resultsCount_few: '{{count}} events',
  resultsCount_other: '{{count}} events',
  mediaArchiveTag: 'Media archive',

  kindDEMONSTRATION: 'Practical demonstration',
  kindVISIT: 'Thematic visit',
  kindWORKSHOP: 'Workshop',
  kindCONFERENCE: 'Conference',
  kindTRAINING_EVENT: 'Training session',
  kindSCIENTIFIC: 'Scientific event',

  backToCalendar: 'Back to the calendar',
  objectives: 'Objectives',
  agenda: 'Agenda',
  program: 'Programme',
  speakers: 'Speakers and specialists involved',
  partners: 'Partners',
  acceptedPapers: 'Accepted papers',
  documents: 'Documents',
  mediaArchive: 'Media archive',
  videoUnsupported: 'Your browser cannot play this video. You can download it below the player.',
  downloadFile: 'Download',
  downloadFileAria: 'Download {{name}}',
  themes: 'Themes',
  eligibility: 'Eligibility criteria',
  participation: 'Participation',
  alreadyRegistered: 'You are registered for this event.',
  cancelRegistration: 'Withdraw my registration',
  cancelRegistrationConfirm:
    'Withdraw your registration for this event? You can register again until it starts.',
  registrationCancelled: 'Your registration was withdrawn.',
  registrationOpen: 'Registration is open to signed-in users.',
  register: 'Register for this event',
  registered: 'Your registration has been recorded. Details will arrive via M-Notify.',
  registerSignIn: 'Sign in to register:',
  registerSignInLink: 'Sign in with M-Pass',
  registerFailed: 'The registration could not be recorded (you may already be registered).',
  concluded: 'This event has concluded',
  concludedWithMedia: 'This event has concluded — the materials are in the media archive.',
  feedbackHeading: 'Evaluate this event',
  feedbackRatingLabel: 'Rating (1–5)',
  feedbackCommentsLabel: 'Comments (optional)',
  feedbackPresentationLabel: 'Quality of the presentations (1–5)',
  feedbackNeedsLabel: 'What training do you need next? (optional)',
  feedbackSubmit: 'Submit evaluation',
  feedbackSubmitting: 'Submitting…',
  feedbackAlready: 'You rated this event: {{rating}}/5.',
  feedbackError: 'The evaluation could not be submitted.',
  ratingsSuffix: 'participant ratings',
  presentationRatings: 'Quality of the presentations',
  noRatings: 'No ratings yet.',
  notFound: 'Event not found.',
  loading: 'Loading…',
} satisfies Translation<keyof typeof ro>;

export const publicEvents = { ro, en } satisfies Dictionary;

export type PublicEventsKey = keyof typeof ro;
